import React, { useState, useRef, useEffect } from 'react';

const METRICS = [
  { value: 'residential_cents_kwh', label: 'Residential Rate', category: 'rates' },
  { value: 'commercial_cents_kwh', label: 'Commercial Rate', category: 'rates' },
  { value: 'industrial_cents_kwh', label: 'Industrial Rate', category: 'rates' },
  { value: 'saidi_without_med', label: 'SAIDI (Outage Duration)', category: 'reliability' },
  { value: 'saifi_without_med', label: 'SAIFI (Outage Frequency)', category: 'reliability' },
  { value: 'ami_deployment_pct', label: 'AMI Deployment %', category: 'infrastructure' },
  { value: 'renewable_pct', label: 'Renewable %', category: 'environmental' },
  { value: 'co2_per_customer', label: 'CO2 per Customer', category: 'environmental' },
];

function Header({
  selectedYear,
  onYearChange,
  selectedMetric,
  onMetricChange,
  availableYears,
  summary,
  utilities,
  onUtilitySelect
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search utilities when query changes
  useEffect(() => {
    if (!searchQuery.trim() || !utilities) {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = Object.entries(utilities)
      .filter(([uid, u]) => {
        const displayName = (u.display_name || '').toLowerCase();
        const legalName = (u.name || '').toLowerCase();
        const parent = (u.parent_company || '').toLowerCase();
        const states = (u.states || []).join(' ').toLowerCase();

        return displayName.includes(query) ||
               legalName.includes(query) ||
               parent.includes(query) ||
               states.includes(query);
      })
      .map(([uid, u]) => ({
        id: uid,
        displayName: u.display_name || u.name,
        legalName: u.name,
        parent: u.parent_company,
        states: u.states || []
      }))
      .slice(0, 10); // Limit to 10 results

    setSearchResults(results);
    setSelectedIndex(-1); // Reset selection when results change
  }, [searchQuery, utilities]);

  // Handle keyboard navigation in search results
  const handleKeyDown = (e) => {
    if (!showDropdown || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleSelectUtility(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSelectUtility = (utility) => {
    onUtilitySelect(utility.id);
    setSearchQuery('');
    setShowDropdown(false);
  };

  return (
    <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
      {/* Logo and title */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center">
          <span className="text-2xl mr-2">&#9889;</span>
          <div className="max-w-md">
            <h1 className="text-lg font-bold text-gray-900">US Utilities Dashboard</h1>
            <p className="text-xs text-gray-500 mt-1">
              Find out how your electric utility compares to the national average on rates, reliability, and clean energy. Data sourced from EIA-861 annual reports (2012-2024).
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {summary ? `${summary.total_utilities} investor-owned utilities` : 'Loading...'}
            </p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative" ref={searchRef}>
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search utilities..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={handleKeyDown}
            className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Search Dropdown */}
        {showDropdown && searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-auto">
            {searchResults.map((utility, index) => (
              <button
                key={utility.id}
                onClick={() => handleSelectUtility(utility)}
                className={`w-full px-4 py-3 text-left border-b border-gray-100 last:border-b-0 transition-colors ${
                  index === selectedIndex ? 'bg-blue-100' : 'hover:bg-blue-50'
                }`}
              >
                <div className="font-medium text-gray-900">{utility.displayName}</div>
                {(utility.parent || utility.legalName !== utility.displayName) && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {utility.legalName !== utility.displayName && utility.legalName}
                    {utility.parent && utility.legalName !== utility.displayName && ' · '}
                    {utility.parent}
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-0.5">
                  {utility.states.join(', ')}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* No results message */}
        {showDropdown && searchQuery.trim() && searchResults.length === 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4 text-center text-gray-500 text-sm">
            No utilities found
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center space-x-4">
        {/* Year selector */}
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-600">Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => onYearChange(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Metric selector */}
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-600">Color by:</label>
          <select
            value={selectedMetric}
            onChange={(e) => onMetricChange(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px]"
          >
            <optgroup label="Rates">
              {METRICS.filter(m => m.category === 'rates').map(metric => (
                <option key={metric.value} value={metric.value}>{metric.label}</option>
              ))}
            </optgroup>
            <optgroup label="Reliability">
              {METRICS.filter(m => m.category === 'reliability').map(metric => (
                <option key={metric.value} value={metric.value}>{metric.label}</option>
              ))}
            </optgroup>
            <optgroup label="Infrastructure">
              {METRICS.filter(m => m.category === 'infrastructure').map(metric => (
                <option key={metric.value} value={metric.value}>{metric.label}</option>
              ))}
            </optgroup>
            <optgroup label="Environmental">
              {METRICS.filter(m => m.category === 'environmental').map(metric => (
                <option key={metric.value} value={metric.value}>{metric.label}</option>
              ))}
            </optgroup>
          </select>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center space-x-2">
        <span className="text-xs text-gray-500 mr-2">Performance:</span>
        <div className="flex items-center space-x-1">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10b981' }}></div>
          <span className="text-xs text-gray-600">Better</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#fbbf24' }}></div>
          <span className="text-xs text-gray-600">Average</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#ef4444' }}></div>
          <span className="text-xs text-gray-600">Worse</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#d1d5db' }}></div>
          <span className="text-xs text-gray-600">No Data</span>
        </div>
      </div>
    </header>
  );
}

export default Header;
